import React, { useState } from "react";
import PropTypes from "prop-types";
import {
    Row,
    Col,
    Offcanvas,
    OffcanvasHeader,
    OffcanvasBody,
} from "reactstrap";

export default function DataCard2({ indicators = [] }) {
    const [selected, setSelected] = useState(null);

    const toggleOffcanvas = (item) => setSelected(item);

    // Helper to render trend with arrow and percent
    const renderTrend = (comparison) => {
        if (!comparison) return null;

        // Expecting comparison like "3" or "-2" (number difference)
        let percent = 0;
        let isUp = true;

        // If comparison is a string like "↑ 3%" or "-2", parse it
        const match = comparison.match(/(-?\d+\.?\d*)/);
        if (match) {
            percent = parseFloat(match[1]);
            isUp = percent >= 0;
        }

        return (
            <div className="flex justify-end gap-1 mt-0 w-full text-xs">
                <span className={isUp ? "text-green-500" : "text-red-500"}>
                    {isUp ? "▲" : "▼"} {Math.abs(percent)}%
                </span>
                <span className="text-gray-400">from last quarter</span>
            </div>
        );
    };

    return (
        <>
            <Row className="g-3">
                {indicators.map((item, idx) => (
                    <Col key={idx} xs="12" sm="6" md="2" lg="2">
                        <button
                            onClick={() => toggleOffcanvas(item)}
                            className="w-full flex items-center gap-3 px-3 py-3 rounded-xl shadow-sm bg-white border border-slate-200 hover:shadow-md transition-all cursor-pointer"
                        >
                            {/* Icon Box */}
                            <div
                                className={`w-10 h-10 flex items-center justify-center rounded-md ${item.bgColor}`}
                            >
                                {React.isValidElement(item.icon)
                                    ? React.cloneElement(item.icon, {
                                        className: `${item.iconColor} w-5 h-5`,
                                    })
                                    : null}
                            </div>

                            {/* Title + Value + Trend */}
                            <div className="flex-1 text-end">
                                <div className="text-xs text-slate-500">{item.title}</div>
                                <div className="text-lg font-semibold text-slate-800 text-right">
                                    {item.value}
                                </div>
                                <div className="text-end mt-1">
                                    {item.comparison && renderTrend(item.comparison)}
                                </div>

                            </div>
                        </button>
                    </Col>
                ))}
            </Row>

            {/* Offcanvas */}
            <Offcanvas
                isOpen={!!selected}
                toggle={() => setSelected(null)}
                direction="end"
                className="w-96"
            >
                <OffcanvasHeader toggle={() => setSelected(null)}>
                    {selected?.title || "Details"}
                </OffcanvasHeader>

                <OffcanvasBody>
                    {selected ? (
                        <div className="space-y-4">
                            <div
                                className={`w-12 h-12 rounded-xl flex items-center justify-center shadow ${selected.bgColor}`}
                            >
                                {React.isValidElement(selected.icon)
                                    ? React.cloneElement(selected.icon, {
                                        className: `${selected.iconColor} w-6 h-6`,
                                    })
                                    : null}
                            </div>

                            <div className="text-2xl font-bold text-slate-800">
                                {selected.value}
                            </div>

                            {/* Trend */}
                            {selected.comparison && renderTrend(selected.comparison)}

                            {/* Benchmark */}
                            {selected.benchmark && (
                                <div className="text-sm text-gray-400">
                                    Benchmark: {selected.benchmark}
                                </div>
                            )}

                            <p className="text-slate-600 text-sm">
                                Additional insights for <b>{selected.title}</b> can go here.
                            </p>
                        </div>
                    ) : (
                        <p className="text-slate-500">Select a card to view details.</p>
                    )}
                </OffcanvasBody>
            </Offcanvas>
        </>
    );
}

DataCard2.propTypes = {
    indicators: PropTypes.arrayOf(
        PropTypes.shape({
            icon: PropTypes.node.isRequired,
            title: PropTypes.string.isRequired,
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
                .isRequired,
            bgColor: PropTypes.string,
            iconColor: PropTypes.string,
            comparison: PropTypes.string, // now should be number string like "3" or "-2"
            benchmark: PropTypes.string,
        })
    ),
};
